// PATH: src/components/catalog/forms/FormDialog.tsx
import * as React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useToast } from "../../ui";
import { writeErrorMessage, type EntityKind } from "../catalogModel";
import { useCatalog } from "../CatalogContext";

interface Props {
  open: boolean;
  title: React.ReactNode;
  /** Une phrase sous le titre : où l'on est (« dans Lycée »). */
  context?: React.ReactNode;
  entity: EntityKind;
  submitLabel?: string;
  /** Message du toast en cas de succès. */
  successMessage: string;
  maxWidth?: "xs" | "sm" | "md";
  /** Valide le formulaire ; `false` = rester ouvert (les erreurs sont affichées par le formulaire). */
  validate: () => boolean;
  /** Écrit ; une exception est affichée dans le dialogue, traduite. */
  onSubmit: () => Promise<void>;
  onClose: () => void;
  /** Appelé après succès et relecture (ex. ouvrir l'élément créé). */
  onSaved?: () => void;
  /** Bloque l'enregistrement (téléversement en cours…). */
  busy?: boolean;
  children: React.ReactNode;
}

/** Coque commune des formulaires du catalogue : titre, erreurs, Annuler / Enregistrer. */
export const FormDialog: React.FC<Props> = ({
  open,
  title,
  context,
  entity,
  submitLabel = "Enregistrer",
  successMessage,
  maxWidth = "xs",
  validate,
  onSubmit,
  onClose,
  onSaved,
  busy,
  children,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const toast = useToast();
  const { reload } = useCatalog();
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (saving || busy) return;
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit();
      toast.success(successMessage);
      await reload();
      onClose();
      onSaved?.();
    } catch (err) {
      setError(writeErrorMessage(err, entity));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
      slotProps={{ paper: { component: "form", onSubmit: submit, noValidate: true } as object }}
    >
      <DialogTitle>
        {title}
        {context && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
            {context}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {children}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving || busy}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FormDialog;
